import type { StudentDocument } from "../types";
import { Icons } from "../utils/icons";
import { canWrite, writeOnly } from "../utils/permissions";
import { esc } from "../utils/html";

const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** Gleicher Umschaltpunkt wie die App-Shell in styles.css: sobald die Sidebar zum
 *  Drawer wird, ist fuer die sechsspaltige Tabelle kein Platz mehr. */
const MOBILE_MQ = "(max-width: 768px)";

export interface DocsApi {
  list: () => Promise<StudentDocument[]>;
  upload: (file: File) => Promise<StudentDocument>;
  download: (docId: number, filename: string) => Promise<void>;
  delete: (docId: number) => Promise<void>;
  rename: (docId: number, newOriginalName: string) => Promise<StudentDocument>;
  /** Optional: mehrere Dokumente zu einem PDF zusammenführen. Wenn gesetzt, erscheint
   *  ein Merge-Button in der Bulk-Action-Bar (≥ 2 Auswahl-Items). */
  merge?: (docIds: number[], filename: string) => Promise<void>;
}

/** canUpload=false blendet den Upload-Bereich aus (Schüler-Portal, wenn die Schule
 *  die Funktion "Eigene Dokumente hochladen" abgeschaltet hat). Die Zeilen-Aktionen
 *  steuert das Backend über canDelete/canRename pro Dokument. */
export function renderDocumentsTab(opts: { canUpload?: boolean } = {}): string {
  // Betrachter dürfen Dokumente lesen und herunterladen, aber nichts hochladen.
  const canUpload = opts.canUpload !== false && canWrite();
  return `
    ${canUpload ? `
    <div id="docs-dropzone"
      style="border:2px dashed var(--primary-light);border-radius:.5rem;padding:1.5rem 1rem;background:var(--primary-50);text-align:center;cursor:pointer;transition:border-color .15s,background .15s;margin-bottom:.75rem">
      <div style="display:inline-block;width:32px;height:32px;color:var(--primary);margin-bottom:.4rem">${Icons.arrowUpTray}</div>
      <div id="docs-dropzone-label" style="font-size:.9rem;color:var(--gray-700)">
        <span style="color:var(--primary);font-weight:600">Datei auswählen</span>
        oder hierher ziehen
      </div>
      <div style="font-size:.75rem;color:var(--gray-500);margin-top:.3rem">
        PDF, JPG, PNG &middot; max. 10&nbsp;MB
      </div>
      <input type="file" id="docs-file-input" hidden
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" />
      <input type="file" id="docs-camera-input" hidden
        accept="image/*" capture="environment" />
      <div id="docs-camera-row" hidden style="margin-top:.75rem">
        <button type="button" class="btn btn-secondary btn-sm" id="docs-camera-btn"
          style="display:inline-flex;align-items:center;gap:.4rem">
          <span style="display:inline-block;width:16px;height:16px">${Icons.camera}</span>
          Foto aufnehmen
        </button>
      </div>
    </div>` : ""}
    <div id="docs-action-bar" class="docs-action-bar" hidden>
      <span id="docs-action-count" style="font-size:.875rem;color:var(--primary-dark);font-weight:500">0 Dokumente ausgewählt</span>
      <div class="docs-action-btns">
        <button class="btn btn-secondary btn-sm" id="btn-merge-sel-docs" hidden disabled>Als ein PDF herunterladen</button>
        ${writeOnly(`<button class="btn btn-secondary btn-sm" id="btn-ren-sel-doc" disabled>Umbenennen</button>
        <button class="btn btn-danger btn-sm" id="btn-del-sel-docs" disabled>Löschen</button>`)}
      </div>
    </div>
    <div id="docs-list">
      <p style="color:var(--gray-500);font-size:.9rem;padding:.5rem 0">Lade Dokumente…</p>
    </div>`;
}

export function attachDocumentsListeners(api: DocsApi, container: HTMLElement) {
  // Ohne Upload-Bereich (renderDocumentsTab mit canUpload:false) fehlen diese Elemente.
  const dropzone = container.querySelector<HTMLElement>("#docs-dropzone");
  const dropLabel = container.querySelector<HTMLElement>("#docs-dropzone-label");
  const fileInput = container.querySelector<HTMLInputElement>("#docs-file-input");
  const cameraInput = container.querySelector<HTMLInputElement>("#docs-camera-input");
  const cameraRow = container.querySelector<HTMLElement>("#docs-camera-row");
  const cameraBtn = container.querySelector<HTMLButtonElement>("#docs-camera-btn");
  const actionBar = container.querySelector<HTMLElement>("#docs-action-bar")!;
  const actionCount = container.querySelector<HTMLElement>("#docs-action-count")!;
  // Fehlen bei Betrachtern: Löschen und Umbenennen sind dann ausgeblendet.
  const delSelBtn = container.querySelector<HTMLButtonElement>("#btn-del-sel-docs");
  const renSelBtn = container.querySelector<HTMLButtonElement>("#btn-ren-sel-doc");
  const mergeSelBtn = container.querySelector<HTMLButtonElement>("#btn-merge-sel-docs")!;
  const listEl = container.querySelector<HTMLElement>("#docs-list")!;
  const defaultDropLabel = dropLabel?.innerHTML ?? "";

  if (api.merge) mergeSelBtn.hidden = false;

  const ctx: SelectionCtx = { actionBar, actionCount, delSelBtn, renSelBtn, mergeSelBtn, lastDocs: [] };

  if (dropzone && dropLabel && fileInput) {
    // Kamera-Button nur auf Touch-Geräten anzeigen (auf Desktop wäre er
    // identisch zum normalen Datei-Dialog).
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouch && cameraRow) cameraRow.hidden = false;

    dropzone.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).closest("#docs-camera-row")) return;
      fileInput.click();
    });

    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (file) void handleUpload(api, file, listEl, ctx, dropLabel, defaultDropLabel, fileInput);
    });

    if (cameraBtn && cameraInput) {
      cameraBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        cameraInput.click();
      });
      cameraInput.addEventListener("change", () => {
        const file = cameraInput.files?.[0];
        if (file) void handleUpload(api, file, listEl, ctx, dropLabel, defaultDropLabel, cameraInput);
      });
    }

    dropzone.addEventListener("dragover", e => {
      e.preventDefault();
      dropzone.style.borderColor = "var(--primary)";
      dropzone.style.background = "var(--primary-light)";
    });
    dropzone.addEventListener("dragleave", () => {
      dropzone.style.borderColor = "var(--primary-light)";
      dropzone.style.background = "var(--primary-50)";
    });
    dropzone.addEventListener("drop", e => {
      e.preventDefault();
      dropzone.style.borderColor = "var(--primary-light)";
      dropzone.style.background = "var(--primary-50)";
      const file = e.dataTransfer?.files?.[0];
      if (file) void handleUpload(api, file, listEl, ctx, dropLabel, defaultDropLabel, fileInput);
    });
  }

  delSelBtn?.addEventListener("click", async () => {
    const ids = selectedIds(listEl);
    if (ids.length === 0) return;
    if (!confirm(`${ids.length} Dokument(e) wirklich löschen?`)) return;
    delSelBtn.disabled = true;
    try {
      for (const id of ids) {
        await api.delete(id);
      }
    } catch (err: any) {
      alert("Löschen fehlgeschlagen: " + (err?.message || "unbekannter Fehler"));
    } finally {
      await refreshList(api, listEl, ctx);
    }
  });

  renSelBtn?.addEventListener("click", () => {
    const ids = selectedIds(listEl);
    if (ids.length !== 1) return;
    const doc = ctx.lastDocs.find(d => d.id === ids[0]);
    if (!doc || !doc.canRename) return;
    openRenameModal(api, doc, () => refreshList(api, listEl, ctx));
  });

  mergeSelBtn.addEventListener("click", async () => {
    if (!api.merge) return;
    const ids = selectedIds(listEl);
    if (ids.length < 2) return;
    const filename = `dokumente-${new Date().toISOString().slice(0, 10)}.pdf`;
    mergeSelBtn.disabled = true;
    const orig = mergeSelBtn.textContent;
    mergeSelBtn.textContent = "Erstelle PDF…";
    try {
      await api.merge(ids, filename);
    } catch (err: any) {
      alert("Zusammenführen fehlgeschlagen: " + (err?.message || "unbekannter Fehler"));
    } finally {
      mergeSelBtn.textContent = orig;
      updateSelectionState(listEl, ctx);
    }
  });

  // Bei Wechsel zwischen Mobile-Card- und Desktop-Tabellen-Layout neu rendern
  // (kein erneuter API-Call). isConnected-Guard verhindert No-op-Arbeit, wenn
  // der enthaltende Modal/Container längst entfernt wurde.
  const mq = window.matchMedia(MOBILE_MQ);
  mq.addEventListener("change", () => {
    if (listEl.isConnected) rerenderList(api, listEl, ctx);
  });

  refreshList(api, listEl, ctx);
}

type SelectionCtx = {
  actionBar: HTMLElement;
  actionCount: HTMLElement;
  delSelBtn: HTMLButtonElement | null;
  renSelBtn: HTMLButtonElement | null;
  mergeSelBtn: HTMLButtonElement;
  lastDocs: StudentDocument[];
};

async function handleUpload(
  api: DocsApi,
  file: File,
  listEl: HTMLElement,
  ctx: SelectionCtx,
  dropLabel: HTMLElement,
  defaultDropLabel: string,
  fileInput: HTMLInputElement
) {
  const nameLower = file.name.toLowerCase();
  const isImageFromCapture =
    file.type.startsWith("image/") && (nameLower === "image.jpg" || nameLower === "");
  if (!isImageFromCapture && !ALLOWED_EXT.some(ext => nameLower.endsWith(ext))) {
    alert("Nur PDF, JPG oder PNG erlaubt.");
    fileInput.value = "";
    return;
  }
  if (file.size > MAX_SIZE_BYTES) {
    alert("Datei überschreitet 10 MB.");
    fileInput.value = "";
    return;
  }

  dropLabel.textContent = `Lade „${file.name}" hoch…`;
  try {
    await api.upload(file);
    await refreshList(api, listEl, ctx);
  } catch (err: any) {
    alert("Upload fehlgeschlagen: " + (err?.error || err?.message || "unbekannter Fehler"));
  } finally {
    dropLabel.innerHTML = defaultDropLabel;
    fileInput.value = "";
  }
}

function isMobileLayout(): boolean {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_MQ).matches;
}

async function refreshList(api: DocsApi, listEl: HTMLElement, ctx: SelectionCtx) {
  try {
    const docs = await api.list();
    ctx.lastDocs = docs;
    rerenderList(api, listEl, ctx);
  } catch (err: any) {
    ctx.lastDocs = [];
    listEl.innerHTML = `<p style="color:var(--red-700);font-size:.9rem">Fehler beim Laden: ${esc(err.message)}</p>`;
    updateSelectionState(listEl, ctx);
  }
}

/** Re-rendert die Liste aus ctx.lastDocs — getrennt von refreshList, damit der
 * Resize-Listener nicht erneut die API aufruft. */
function rerenderList(api: DocsApi, listEl: HTMLElement, ctx: SelectionCtx) {
  const docs = ctx.lastDocs;
  if (docs.length === 0) {
    listEl.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;gap:.5rem;padding:2rem 1rem;color:var(--gray-500);text-align:center">
        <span style="display:inline-flex;align-items:center;justify-content:center;width:48px;height:48px;border-radius:.5rem;background:var(--gray-100);color:var(--gray-400)">
          <span style="display:inline-block;width:26px;height:26px">${Icons.documentText}</span>
        </span>
        <p style="font-size:.9rem;margin:0">Noch keine Dokumente vorhanden.</p>
      </div>`;
  } else if (isMobileLayout()) {
    listEl.innerHTML = renderCards(docs);
  } else {
    listEl.innerHTML = renderTable(docs);
  }
  bindListHandlers(api, listEl, ctx);
  updateSelectionState(listEl, ctx);
}

function fileTypeIcon(filename: string): string {
  const lower = filename.toLowerCase();
  let icon: string;
  let color: string;
  let bg: string;
  if (lower.endsWith(".pdf")) {
    icon = Icons.documentText;
    color = "var(--red-500)";
    bg = "var(--red-50)";
  } else if (lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png")) {
    icon = Icons.photo;
    color = "var(--green-700)";
    bg = "var(--green-50)";
  } else {
    icon = Icons.documentText;
    color = "var(--gray-500)";
    bg = "var(--gray-100)";
  }
  return `<span style="display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:.5rem;background:${bg};color:${color};flex-shrink:0">
    <span style="display:inline-block;width:20px;height:20px">${icon}</span>
  </span>`;
}

function renderCards(docs: StudentDocument[]): string {
  return `<div style="display:flex;flex-direction:column;gap:.5rem">${docs.map(renderCard).join("")}</div>`;
}

function renderCard(d: StudentDocument): string {
  const meta = `${formatSize(d.sizeBytes)} · ${formatDate(d.uploadedAt)}${d.uploadedByName ? ` · ${esc(fmtUploaderShort(d))}` : ""}`;
  const actions: string[] = [
    `<button class="doc-download doc-icon-btn" data-id="${d.id}" data-name="${esc(d.originalName)}"
       title="Herunterladen" aria-label="Herunterladen"
       style="display:inline-flex;align-items:center;justify-content:center;min-width:40px;min-height:40px;padding:.4rem;background:none;border:none;border-radius:.375rem;color:var(--gray-500);cursor:pointer;transition:background .15s,color .15s">
       <span style="display:inline-block;width:20px;height:20px">${Icons.arrowDownTray}</span>
     </button>`,
  ];
  // Betrachter dürfen herunterladen und zusammenführen, aber nicht umbenennen oder löschen.
  if (d.canRename && canWrite()) {
    actions.push(`<button class="btn btn-secondary btn-sm doc-rename" data-id="${d.id}"
       title="Umbenennen" aria-label="Umbenennen"
       style="display:inline-flex;align-items:center;justify-content:center;min-width:40px;min-height:40px;padding:.4rem">
       <span style="display:inline-block;width:18px;height:18px">${Icons.pencilSquare}</span>
     </button>`);
  }
  if (d.canDelete && canWrite()) {
    actions.push(`<button class="btn btn-danger btn-sm doc-delete" data-id="${d.id}"
       title="Löschen" aria-label="Löschen"
       style="display:inline-flex;align-items:center;justify-content:center;min-width:40px;min-height:40px;padding:.4rem">
       <span style="display:inline-block;width:18px;height:18px">${Icons.trash}</span>
     </button>`);
  }
  return `
    <div style="border:1px solid var(--gray-200);border-radius:.5rem;padding:.75rem;background:#fff">
      <div style="display:flex;gap:.75rem;align-items:flex-start;min-width:0">
        ${fileTypeIcon(d.originalName)}
        <div style="min-width:0;flex:1">
          <div style="display:flex;flex-direction:column;gap:.25rem;margin-bottom:.5rem">
            <div style="font-weight:500;font-size:.95rem;line-height:1.3;word-break:break-word;color:var(--gray-900)">${esc(d.originalName)}</div>
            <div style="font-size:.75rem;color:var(--gray-500)">${meta}</div>
          </div>
          <div style="display:flex;gap:.4rem;justify-content:flex-end;flex-wrap:wrap">${actions.join("")}</div>
        </div>
      </div>
    </div>`;
}

/** Anzeige-Label für den Uploader-Typ; null für Schul-Uploads (kein Badge nötig). */
function uploaderKindLabel(kind: StudentDocument["uploadedByKind"]): string | null {
  switch (kind) {
    case "student": return "Schüler";
    case "practice": return "Praktikumsstelle";
    default: return null;
  }
}

function fmtUploaderShort(d: StudentDocument): string {
  if (!d.uploadedByName) return "";
  const label = uploaderKindLabel(d.uploadedByKind);
  return label ? `${d.uploadedByName} (${label})` : d.uploadedByName;
}

function renderTable(docs: StudentDocument[]): string {
  const anyDeletable = docs.some(d => d.canDelete);
  const rows = docs.map(d => `
    <tr>
      <td onclick="event.stopPropagation()">
        ${d.canDelete
          ? `<input type="checkbox" class="row-sel" data-id="${d.id}" data-can-rename="${d.canRename ? 1 : 0}" />`
          : ""}
      </td>
      <td>
        <div style="display:flex;align-items:center;gap:.75rem;min-width:0">
          ${fileTypeIcon(d.originalName)}
          <span style="font-weight:500;color:var(--gray-900);overflow:hidden;text-overflow:ellipsis" title="${esc(d.originalName)}">${esc(d.originalName)}</span>
        </div>
      </td>
      <td class="text-muted" style="white-space:nowrap">${formatSize(d.sizeBytes)}</td>
      <td style="white-space:nowrap;color:var(--gray-700)">${formatDate(d.uploadedAt)}</td>
      <td>${fmtUploaderCell(d)}</td>
      <td style="width:56px;text-align:center">
        <button class="doc-download doc-icon-btn" data-id="${d.id}" data-name="${esc(d.originalName)}"
          title="Herunterladen" aria-label="Herunterladen"
          style="display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;padding:0;background:none;border:none;border-radius:.375rem;color:var(--gray-500);cursor:pointer;transition:background .15s,color .15s">
          <span style="display:inline-block;width:18px;height:18px">${Icons.arrowDownTray}</span>
        </button>
      </td>
    </tr>`).join("");
  return `
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th style="width:36px">${anyDeletable ? `<input type="checkbox" id="sel-all-docs" style="width:16px;height:16px;accent-color:var(--primary);cursor:pointer" />` : ""}</th>
            <th>Name</th>
            <th>Größe</th>
            <th>Datum</th>
            <th>Hochgeladen von</th>
            <th style="width:56px;text-align:center">Download</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

function bindListHandlers(api: DocsApi, listEl: HTMLElement, ctx: SelectionCtx) {
  const selAll = listEl.querySelector<HTMLInputElement>("#sel-all-docs");
  selAll?.addEventListener("change", () => {
    listEl.querySelectorAll<HTMLInputElement>(".row-sel").forEach(b => b.checked = selAll.checked);
    updateSelectionState(listEl, ctx);
  });

  listEl.querySelectorAll<HTMLInputElement>(".row-sel").forEach(cb => {
    cb.addEventListener("change", () => updateSelectionState(listEl, ctx));
  });

  listEl.querySelectorAll<HTMLButtonElement>(".doc-download").forEach(btn => {
    btn.addEventListener("click", async () => {
      const docId = Number(btn.dataset.id);
      const name = btn.dataset.name || `dokument-${docId}`;
      try {
        await api.download(docId, name);
      } catch (err: any) {
        alert("Download fehlgeschlagen: " + err.message);
      }
    });
  });

  // Per-Karten-Aktionen (nur im Mobile-Card-Layout vorhanden)
  listEl.querySelectorAll<HTMLButtonElement>(".doc-rename").forEach(btn => {
    btn.addEventListener("click", () => {
      const docId = Number(btn.dataset.id);
      const doc = ctx.lastDocs.find(d => d.id === docId);
      if (!doc || !doc.canRename) return;
      openRenameModal(api, doc, () => refreshList(api, listEl, ctx));
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>(".doc-delete").forEach(btn => {
    btn.addEventListener("click", async () => {
      const docId = Number(btn.dataset.id);
      const doc = ctx.lastDocs.find(d => d.id === docId);
      if (!doc || !doc.canDelete) return;
      if (!confirm(`„${doc.originalName}" wirklich löschen?`)) return;
      btn.disabled = true;
      try {
        await api.delete(docId);
      } catch (err: any) {
        alert("Löschen fehlgeschlagen: " + (err?.message || "unbekannter Fehler"));
      } finally {
        await refreshList(api, listEl, ctx);
      }
    });
  });
}

function updateSelectionState(listEl: HTMLElement, ctx: SelectionCtx) {
  const boxes = [...listEl.querySelectorAll<HTMLInputElement>(".row-sel")];
  const checked = boxes.filter(b => b.checked);
  const selAll = listEl.querySelector<HTMLInputElement>("#sel-all-docs");
  if (selAll) {
    selAll.checked = boxes.length > 0 && checked.length === boxes.length;
    selAll.indeterminate = checked.length > 0 && checked.length < boxes.length;
  }
  ctx.actionBar.hidden = boxes.length === 0;
  if (ctx.delSelBtn) ctx.delSelBtn.disabled = checked.length === 0;
  const checkedRenameable = checked.filter(b => b.dataset.canRename === "1");
  if (ctx.renSelBtn) ctx.renSelBtn.disabled = !(checked.length === 1 && checkedRenameable.length === 1);
  if (!ctx.mergeSelBtn.hidden) ctx.mergeSelBtn.disabled = checked.length < 2;
  ctx.actionCount.textContent =
    checked.length === 1 ? "1 Dokument ausgewählt" : `${checked.length} Dokumente ausgewählt`;
}

function selectedIds(listEl: HTMLElement): number[] {
  return [...listEl.querySelectorAll<HTMLInputElement>(".row-sel")]
    .filter(b => b.checked)
    .map(b => Number(b.dataset.id));
}

function fmtUploader(d: StudentDocument): string {
  if (!d.uploadedByName) return "—";
  const label = uploaderKindLabel(d.uploadedByKind);
  return esc(d.uploadedByName) + (label ? ` (${label})` : "");
}

function fmtUploaderCell(d: StudentDocument): string {
  if (!d.uploadedByName) return `<span class="text-muted">—</span>`;
  const name = esc(d.uploadedByName);
  const label = uploaderKindLabel(d.uploadedByKind);
  if (label) {
    return `<span style="display:inline-flex;align-items:center;gap:.4rem;color:var(--gray-700)">
      ${name}
      <span class="badge badge-blue" style="font-size:.65rem">${esc(label)}</span>
    </span>`;
  }
  return `<span style="color:var(--gray-700)">${name}</span>`;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function splitFilename(name: string): { base: string; ext: string } {
  const i = name.lastIndexOf(".");
  if (i <= 0 || i === name.length - 1) return { base: name, ext: "" };
  return { base: name.slice(0, i), ext: name.slice(i) };
}

/**
 * Eigenständiges Overlay statt des globalen Singleton-Modals — sonst würde
 * close() auch den umgebenden Schüler-Detail-Dialog schließen.
 */
function openRenameModal(api: DocsApi, doc: StudentDocument, onSaved: () => void) {
  const { base, ext } = splitFilename(doc.originalName);
  const overlay = document.createElement("div");
  overlay.style.cssText =
    "position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:10010;display:flex;align-items:center;justify-content:center";
  overlay.innerHTML = `
    <div role="dialog" aria-modal="true"
      style="background:#fff;border-radius:.5rem;box-shadow:0 10px 25px rgba(0,0,0,.2);width:min(28rem,calc(100% - 2rem));max-height:90vh;overflow-y:auto">
      <div style="display:flex;align-items:center;justify-content:space-between;padding:.875rem 1.25rem;border-bottom:1px solid var(--gray-100)">
        <h2 style="font-size:1.05rem;font-weight:600;margin:0">Dokument umbenennen</h2>
        <button type="button" id="rename-close" aria-label="Schließen"
          style="background:none;border:none;font-size:1.25rem;line-height:1;cursor:pointer;color:var(--gray-500)">×</button>
      </div>
      <div style="padding:1rem 1.25rem">
        <label for="rename-input" style="display:block;font-size:.875rem;margin-bottom:.25rem;color:var(--gray-700)">Name</label>
        <input id="rename-input" type="text" value="${esc(base)}"
          style="width:100%;padding:.45rem .55rem;border:1px solid var(--gray-300);border-radius:.25rem;font-size:.95rem">
        ${ext ? `<div style="font-size:.75rem;color:var(--gray-500);margin-top:.4rem">Endung <code>${esc(ext)}</code> bleibt erhalten.</div>` : ""}
        <div style="display:flex;gap:.5rem;justify-content:flex-end;margin-top:1rem">
          <button class="btn btn-secondary" id="rename-cancel">Abbrechen</button>
          <button class="btn btn-primary" id="rename-save">Speichern</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const input = overlay.querySelector<HTMLInputElement>("#rename-input")!;
  const saveBtn = overlay.querySelector<HTMLButtonElement>("#rename-save")!;

  const close = () => {
    document.removeEventListener("keydown", onKey);
    overlay.remove();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") { e.stopPropagation(); close(); }
  };
  document.addEventListener("keydown", onKey);
  overlay.addEventListener("click", e => { if (e.target === overlay) close(); });
  overlay.querySelector<HTMLButtonElement>("#rename-close")!.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("#rename-cancel")!.addEventListener("click", close);

  input.focus();
  input.select();
  saveBtn.addEventListener("click", async () => {
    const next = input.value.trim();
    if (!next) return;
    saveBtn.disabled = true;
    try {
      await api.rename(doc.id, next);
      close();
      onSaved();
    } catch (err: any) {
      alert("Umbenennen fehlgeschlagen: " + (err?.error || err?.message || "unbekannter Fehler"));
      saveBtn.disabled = false;
    }
  });
  input.addEventListener("keydown", e => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveBtn.click();
    }
  });
}
